// Copyright (c) Ping. All rights reserved.
//
// [#2] Receiver side of linking history-transfer (new device).
//
// During linking the new device holds two things it needs to OPEN the history
// the existing device ships: its ephemeral X25519 PRIVATE key (the QR one) and
// the account's user-identity PUBLIC key (from the link-auth envelope). The
// existing device only posts the history AFTER it sees this device finish
// link-verify (seconds later), so we can't consume it inline. Instead the
// linking host stashes {ephemeralPriv, senderIdentityPub} here, and a one-shot
// poller runs post-bootstrap (App.tsx) — it drains the consuming inbox a few
// times over ~30s, opens+verifies each bundle (MessagingClient.openHistoryBundle),
// and replays the events into the local message store, deduped by content hash.

import { MessagingClient } from 'ping-openmls-sdk';

import { useChatSyncStore } from '@ping/ui';

import { log } from '@core/log/log';
import { db } from '@core/persistence/db';

import { consumeHistoryTransferInbox, type PendingHistoryTransfer } from './historyTransfersApi';

interface OpenableClient {
  openHistoryBundle(
    sealed: Uint8Array,
    recipientPriv: Uint8Array,
    senderIdentityPub: Uint8Array,
  ): Promise<HistoryBundleShape>;
}

export interface HistoryBundleShape {
  sender_device_id: Uint8Array;
  conversation_id: Uint8Array;
  events: Array<{
    event_id: Uint8Array;
    wall_ms: number;
    payload: Uint8Array;
    // Original author the sender recorded (account UUID for self-sent, hex
    // device id for received). Absent from pre-`author` SDK bundles — replay
    // then falls back to the bundle sender id (everything shows as received).
    author?: string;
  }>;
}

interface Stash {
  ephemeralPriv: Uint8Array;
  senderIdentityPub: Uint8Array;
}

let stash: Stash | null = null;
// In-flight guard: `consumePendingLinkingHistory` is kicked on every
// authenticated transition AND resumes from IndexedDB, so without this two
// passes could race on the single destructive inbox.
let draining = false;

// Elastic drain window. The existing device only posts history AFTER it detects
// this device in /v1/auth/sessions, claims KeyPackages, admits us to every
// chat, then seals + posts — commonly 5–30s+, longer with many chats / KP
// replenish lag. A short fixed window silently dropped late bundles, so we poll
// for several minutes with backoff and, crucially, PERSIST the open-keys so a
// reload (or tab close) resumes the drain instead of losing them. We only clear
// once history actually arrived or the link is older than the age cap.
const POLL_ATTEMPTS = 24;
const POLL_INTERVAL_MS = 8000;
const PENDING_MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000; // 14 days
const STASH_KEY = '__linking.historyPending.v1';

interface PersistedStash {
  ephemeralPriv: number[];
  senderIdentityPub: number[];
  markedAt: number;
}

async function persistStash(s: Stash): Promise<void> {
  try {
    await db.settings_kv.put({
      key: STASH_KEY,
      value: {
        ephemeralPriv: Array.from(s.ephemeralPriv),
        senderIdentityPub: Array.from(s.senderIdentityPub),
        markedAt: Date.now(),
      } satisfies PersistedStash,
      updatedAt: Date.now(),
    });
  } catch {
    /* best-effort — IndexedDB may be unavailable */
  }
}

async function loadPersistedStash(): Promise<{ stash: Stash; markedAt: number } | null> {
  try {
    const rec = await db.settings_kv.get(STASH_KEY);
    const v = rec?.value as PersistedStash | undefined;
    if (
      v === undefined ||
      !Array.isArray(v.ephemeralPriv) ||
      !Array.isArray(v.senderIdentityPub)
    ) {
      return null;
    }
    return {
      stash: {
        ephemeralPriv: Uint8Array.from(v.ephemeralPriv),
        senderIdentityPub: Uint8Array.from(v.senderIdentityPub),
      },
      markedAt: typeof v.markedAt === 'number' ? v.markedAt : Date.now(),
    };
  } catch {
    return null;
  }
}

async function clearPersistedStash(): Promise<void> {
  try {
    await db.settings_kv.delete(STASH_KEY);
  } catch {
    /* best-effort */
  }
}

/** Linking host calls this once it has the ephemeral priv + account identity pubkey. */
export function stashPendingLinkingHistory(s: Stash): void {
  stash = s;
  // Persist so the drain can resume across reloads / tab close (the existing
  // device may post history after this tab is gone).
  void persistStash(s);
}

export function hasPendingLinkingHistory(): boolean {
  return stash !== null;
}

/**
 * Pending across reloads: true if a linking history hand-off is still owed —
 * either an in-memory stash (fresh link this session) OR a persisted one (a
 * prior session linked but history hadn't fully arrived). App.tsx uses this to
 * decide that `consumePendingLinkingHistory` is the SOLE inbox drainer this
 * load (trying both the QR ephemeral key AND the durable delivery key), and to
 * hold back the recovery delivery-key drain — the two must never drain the
 * single destructive inbox concurrently or they'd consume + drop each other's
 * bundles.
 */
export async function isLinkingHistoryPending(): Promise<boolean> {
  if (stash !== null) return true;
  return (await loadPersistedStash()) !== null;
}

function hex(bytes: Uint8Array): string {
  let s = '';
  for (const b of bytes) s += b.toString(16).padStart(2, '0');
  return s;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', bytes as BufferSource);
  return hex(new Uint8Array(digest));
}

/** Minimal AppEvent decode mirroring the incoming-message persist path: text →
 *  payload.body; media kinds → full envelope JSON as body; receipts/other → skip. */
function bodyForReplay(payload: Uint8Array): string | undefined {
  try {
    const text = new TextDecoder().decode(payload);
    const parsed = JSON.parse(text) as { kind?: string; payload?: Record<string, unknown> };
    const kind = parsed.kind;
    if (kind === 'com.ping.text' && typeof parsed.payload?.body === 'string') {
      return parsed.payload.body;
    }
    if (
      kind === 'com.ping.voice' ||
      kind === 'com.ping.gif' ||
      kind === 'com.ping.image' ||
      kind === 'com.ping.file'
    ) {
      return text; // chat surface's per-variant dispatcher decodes the JSON
    }
    return undefined; // receipts / non-renderable — nothing to show
  } catch {
    return undefined;
  }
}

/**
 * Replay an OPENED + VERIFIED history bundle into the local message store,
 * deduped by content hash (SHA-256 of the AppEvent payload) so a message that
 * also arrives live merges cleanly. Shared by the linking drain AND the no-QR
 * recovery peer-history drain (`recoveryPeerHistory.ts`). Returns the count of
 * newly-stored rows. Callers MUST verify the bundle's sender signature (via
 * `openHistoryBundle` against the resolved sender identity pubkey) BEFORE
 * calling this — it does not re-check authenticity.
 */
export async function replayHistoryBundle(bundle: HistoryBundleShape): Promise<number> {
  return replayBundle(bundle);
}

async function replayBundle(bundle: HistoryBundleShape): Promise<number> {
  const conversationId = hex(bundle.conversation_id);
  const fallbackSender = hex(bundle.sender_device_id);
  let replayed = 0;
  for (const evt of bundle.events) {
    const body = bodyForReplay(evt.payload);
    if (body === undefined) continue;
    // Prefer the per-event author the sender recorded (drives self-vs-peer
    // bubble alignment); fall back to the bundle sender for legacy bundles.
    const senderAccountId =
      evt.author !== undefined && evt.author.length > 0 ? evt.author : fallbackSender;
    // content_hash = SHA-256(plaintext) per CR-6 — matches what normal sync
    // would store, so a message that ALSO arrives live dedups cleanly.
    const contentHash = await sha256Hex(evt.payload);
    const existing = await db.messages.where('contentHash').equals(contentHash).first();
    if (existing) continue;
    const id =
      typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `history-${evt.wall_ms}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      await db.messages.put({
        id,
        conversationId,
        epoch: 0,
        serverSeq: evt.wall_ms,
        contentHash,
        senderAccountId,
        body,
        appEventBytes: evt.payload,
        sentAt: evt.wall_ms,
      });
      replayed += 1;
    } catch (err) {
      // `&contentHash` is a UNIQUE index — a message that arrived live between
      // the dedup read above and this write trips a ConstraintError. That just
      // means it's already stored, so swallow it; surface anything else.
      if ((err as { name?: string }).name !== 'ConstraintError') throw err;
    }
  }
  return replayed;
}

/**
 * Drain + replay any linking history this just-linked device was shipped.
 * No-ops unless the linking host stashed the keys. Idempotent: clears the
 * stash when done so a later authenticated transition won't re-run it.
 */
export async function consumePendingLinkingHistory(opts?: {
  // The durable history-delivery PRIV (32 bytes), when this device has published
  // one (server admission ledger). QR-device bundles are sealed to the QR
  // ephemeral key; bundles a NON-QR sibling shipped via the ledger are sealed to
  // this delivery key instead. Siblings share THIS account's identity, so the
  // stash's `senderIdentityPub` verifies their bundles too — we just need the
  // extra recipient key to HPKE-open them. We try the ephemeral key first, then
  // this, per transfer. Passing it makes this drain the SOLE inbox consumer
  // (see `isLinkingHistoryPending`), so the recovery drain won't race it.
  deliveryPriv?: Uint8Array | null;
}): Promise<void> {
  const deliveryPriv = opts?.deliveryPriv ?? null;
  // Prefer the in-memory stash (fresh link this session); otherwise rehydrate
  // from IndexedDB so a reload after an interrupted/never-delivered drain
  // resumes. No-op when neither is present.
  if (draining) return;
  let s = stash;
  let markedAt = Date.now();
  if (s === null) {
    const persisted = await loadPersistedStash();
    if (persisted === null) return;
    s = persisted.stash;
    markedAt = persisted.markedAt;
  }
  if (draining) return; // re-check after the await above
  draining = true;
  stash = null; // don't run two in-memory passes concurrently

  // Surface the "Syncing conversations… N%" banner: a stash is present so the
  // existing device is (or will be) shipping us history. The denominator
  // (`batch_total`) and numerator (bundles replayed) fill in as bundles land.
  const syncStore = useChatSyncStore.getState();
  syncStore.startLinkingSync();

  const client = MessagingClient as unknown as OpenableClient;
  let totalReplayed = 0;
  let emptyStreak = 0;
  let sawAny = false;

  for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt++) {
    let transfers: PendingHistoryTransfer[];
    try {
      transfers = await consumeHistoryTransferInbox();
    } catch (err) {
      log.warn({ event: 'linking.history.inboxFailed', message: (err as Error).message });
      transfers = [];
    }
    if (transfers.length === 0) {
      emptyStreak += 1;
      // Stop once we've drained everything (two empties after seeing data) —
      // or just keep waiting for the first batch up to the attempt budget.
      if (sawAny && emptyStreak >= 2) break;
    } else {
      sawAny = true;
      emptyStreak = 0;
      for (const t of transfers) {
        // Learn the % denominator from `batch_total` BEFORE attempting to open
        // the bundle, so the banner shows a real percentage even if a bundle
        // fails to decrypt/open (otherwise `total` would only be learned inside
        // the success branch below, and any open failure left "Syncing…" with
        // no %). Matches the iOS receiver, which reads batch_total first.
        useChatSyncStore.getState().noteLinkingBundleTotal(t.batchTotal);
        // Try the QR ephemeral key first (the common case — the QR device
        // sealed to it), then the durable delivery key (a ledger sibling sealed
        // to it). The inbox is destructive-on-read, so the ONE drain that owns
        // it must try every key we hold; a key mismatch alone must not drop a
        // bundle another key would open.
        const recipientKeys =
          deliveryPriv !== null ? [s.ephemeralPriv, deliveryPriv] : [s.ephemeralPriv];
        let bundle: HistoryBundleShape | null = null;
        for (const recipientPriv of recipientKeys) {
          try {
            bundle = await client.openHistoryBundle(
              t.sealedBlob,
              recipientPriv,
              s.senderIdentityPub,
            );
            break;
          } catch {
            /* wrong key (or forged) — try the next, else discard below */
          }
        }
        if (bundle === null) {
          // No held key opened it → forged or not-for-us. Logged opaque.
          log.warn({ event: 'linking.history.openFailed', message: 'no key opened transfer' });
          continue;
        }
        const replayed = await replayBundle(bundle);
        totalReplayed += replayed;
        // Advance the % banner: each opened bundle is one conversation of the
        // `batch_total` the sender declared (the denominator is learned from
        // the first transfer that carried it; absent → indeterminate count).
        useChatSyncStore.getState().noteLinkingBundleReplayed(t.batchTotal, replayed);
      }
    }
    if (attempt < POLL_ATTEMPTS - 1) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
    }
  }

  // Only finish (clear the persisted stash) once we ACTUALLY received history
  // this run, or the link is older than the age cap. Otherwise leave the
  // persisted stash so the next authenticated load resumes draining — the
  // existing device's durable reconcile (pendingOutgoingLink) will post the
  // history eventually even if it hadn't by this run.
  const ageExceeded = Date.now() - markedAt > PENDING_MAX_AGE_MS;
  if (sawAny || ageExceeded) {
    await clearPersistedStash();
    log.info({
      event: 'linking.history.replayed',
      messages: totalReplayed,
      received: sawAny,
    });
  } else {
    // Re-stash in memory so a same-session retry (next authenticated
    // transition) can pick up without an IDB round-trip; the persisted copy
    // remains the source of truth across reloads.
    stash = s;
    log.info({ event: 'linking.history.pending', msg: 'not yet delivered; will resume' });
  }
  // Wipe the in-memory ephemeral private key copy only when we're done with it.
  if (sawAny || ageExceeded) {
    s.ephemeralPriv.fill(0);
  }
  // Dismiss the "Syncing conversations…" banner when this run finished draining
  // (received everything, or aged out). If history never arrived this run we
  // leave the banner up — the re-stashed drain will resume and keep it visible.
  if (sawAny || ageExceeded) {
    useChatSyncStore.getState().finishLinkingSync();
  }
  draining = false;
}
